const HEADER_SIZE = 1000;
const HASH_SIZE = 32;

export interface ProjectHeader {
  projectName: string;
  project?: {
    accountId: number;
    projectId: string;
  };
  breed: string; // js,p5
}

export async function calculateHash(data: Uint8Array): Promise<Uint8Array> {
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  return new Uint8Array(hashBuffer);
}

/**
 * Blob 앞에 HEADER_SIZE 바이트의 헤더와 해시를 추가하는 함수
 */
export async function makeProjectFile(zipBlob: Blob, header: ProjectHeader): Promise<Blob> {
  const headerBytes = new TextEncoder().encode(JSON.stringify(header));

  // 헤더가 HEADER_SIZE 바이트보다 작을 경우 나머지 부분을 0으로 채워줍니다.
  let paddedHeaderBytes: Uint8Array;
  if (headerBytes.length < HEADER_SIZE) {
    paddedHeaderBytes = new Uint8Array(HEADER_SIZE);
    paddedHeaderBytes.set(headerBytes);
  } else if (headerBytes.length > HEADER_SIZE) {
    throw new Error(`Header is too long. It must be ${HEADER_SIZE} bytes or less.`);
  } else {
    paddedHeaderBytes = headerBytes;
  }

  // 헤더의 해시를 계산합니다.
  const headerHash = await calculateHash(paddedHeaderBytes);

  // Blob에서 ArrayBuffer로 데이터를 읽어옵니다.
  const zipArrayBuffer = await zipBlob.arrayBuffer();
  const zipUint8Array = new Uint8Array(zipArrayBuffer);

  // 헤더, 해시, zip 파일을 결합한 새로운 Uint8Array를 반환합니다.
  const fileBytes = new Uint8Array([...paddedHeaderBytes, ...headerHash, ...zipUint8Array]);
  return new Blob([fileBytes], { type: 'application/octet-stream' });
}

/**
 * 결합된 파일에서 헤더, 해시, zip 파일을 분리하고, 해시를 검증하는 함수
 */
export async function splitProjectFile(
  file: Uint8Array,
): Promise<{ header: ProjectHeader; zipFile: Blob }> {
  if (file.length < HEADER_SIZE + HASH_SIZE) {
    // 100바이트의 헤더 + 32바이트의 해시 = 132바이트 이상이어야 함
    throw new Error('The file is too small to contain a valid header and hash.');
  }

  // 파일의 처음 100바이트를 헤더로 추출
  const headerBytes = file.slice(0, HEADER_SIZE);
  const decoder = new TextDecoder('utf-8');
  const header = decoder.decode(headerBytes).replace(/\0+$/, ''); // 패딩된 null 문자 제거

  // 다음 32바이트를 해시로 추출
  const originalHash = file.slice(HEADER_SIZE, HEADER_SIZE + HASH_SIZE);

  // 나머지 바이트를 zip 파일로 추출
  const zipFile = file.slice(HEADER_SIZE + HASH_SIZE);

  // 헤더의 해시를 다시 계산하여 검증
  const calculatedHash = await calculateHash(headerBytes);
  if (!originalHash.every((value, index) => value === calculatedHash[index])) {
    throw new Error('Header has been modified or is corrupted!');
  }

  return {
    header: JSON.parse(header),
    zipFile: new Blob([zipFile], { type: 'application/octet-stream' }),
  };
}
